import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Download, 
  Share2, 
  Printer, 
  X, 
  Smartphone,
  Calendar,
  Hash,
  User,
  Wifi,
  Copy,
  CheckCircle
} from 'lucide-react';
import { toPng, toJpeg } from 'html-to-image';
import { useTranslation } from 'react-i18next';
import Lottie from "lottie-react";
import successAnimation from "../../../../../public/lotties/succcess.json";
import failedAnimation from "../../../../../public/lotties/error.json";

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const RechargeReceipt = ({ transaction, onClose, isOpen }) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [renderError, setRenderError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const receiptRef = useRef(null);
  const isMounted = useRef(true);
  const abortControllerRef = useRef(null);

  // Dark mode detection
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return { date: 'N/A', time: 'N/A' };
    try {
      const date = new Date(dateString);
      const locale = i18n.language === 'prs' || i18n.language === 'ps' ? 'fa-IR' : 'en-US';
      return {
        date: date.toLocaleDateString(locale, { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        }),
        time: date.toLocaleTimeString(locale, { 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      };
    } catch (error) {
      return { date: 'N/A', time: 'N/A' };
    }
  }, [i18n.language]);

  const { date, time } = transaction?.createdAt ? formatDate(transaction.createdAt) : { date: 'N/A', time: 'N/A' };

  const getStatusColor = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'succeeded':
      case 'success':
        return isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800';
      case 'failed':
        return isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800';
      case 'pending':
        return isDark ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return isDark ? 'bg-secondary-900/30 text-secondary-300' : 'bg-secondary-100 text-secondary-800';
      case 'cancelled':
        return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
      default:
        return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  }, [isDark]);

  const getAnimation = useCallback(() => {
    if (!transaction) return null;
    if (transaction?.status === 'succeeded' || transaction?.status === 'success') {
      return successAnimation;
    } else if (transaction?.status === 'failed') {
      return failedAnimation;
    }
    return null;
  }, [transaction]);

  const cleanupOperations = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
      cleanupOperations();
    };
  }, [cleanupOperations]);

  useEffect(() => {
    if (isOpen) {
      const checkRef = () => {
        if (isMounted.current) {
          setIsReady(!!receiptRef.current);
        }
      };
      
      requestAnimationFrame(() => {
        requestAnimationFrame(checkRef);
      });
    } else {
      setIsReady(false);
      cleanupOperations();
    }
  }, [isOpen, cleanupOperations]);

  useEffect(() => {
    if (!isOpen) {
      setIsSharing(false);
      setIsDownloading(false);
      setIsPrinting(false);
      setRenderError(null);
      setCopied(false);
    }
  }, [isOpen]);

  const captureReceipt = useCallback(async () => {
    if (!receiptRef.current) return null;
    abortControllerRef.current = new AbortController();
    
    try {
      const element = receiptRef.current;
      await new Promise(resolve => requestAnimationFrame(resolve));
      
      if (abortControllerRef.current.signal.aborted) {
        return null;
      }
      
      const dataUrl = await toPng(element, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: isDark ? '#1f2937' : '#ffffff',
        cacheBust: true,
        filter: (node) => {
          if (node.tagName === 'SCRIPT' || node.tagName === 'IFRAME') {
            return false;
          }
          if (node.className && typeof node.className === 'string' && 
              node.className.includes('stripe')) {
            return false;
          }
          return true;
        }
      });
      
      return dataUrl;
    } catch (error) {
      console.error('Error capturing receipt:', error);
      return null;
    } finally {
      abortControllerRef.current = null;
    }
  }, [isDark]);

  const downloadAsImage = useCallback(async () => {
    if (!receiptRef.current || isDownloading || !isMounted.current) return;
    
    cleanupOperations();
    
    setIsDownloading(true);
    setRenderError(null);
    
    try {
      const dataUrl = await captureReceipt();
      
      if (!dataUrl || !isMounted.current) {
        throw new Error('Failed to capture receipt');
      }
      
      const link = document.createElement('a');
      link.download = `recharge-receipt-${transaction?.txnNumber || 'order'}-${Date.now()}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
    } catch (error) {
      console.error('Error downloading image:', error);
      if (isMounted.current) {
        setRenderError(t('rechargeReceipt.downloadError') || 'Failed to download. Please try again.');
      }
    } finally {
      if (isMounted.current) {
        setIsDownloading(false);
      }
    }
  }, [transaction?.txnNumber, isDownloading, captureReceipt, cleanupOperations, t]);

  const shareReceipt = useCallback(async () => {
    if (!receiptRef.current || isSharing || !isMounted.current) return;
    
    cleanupOperations();
    
    setIsSharing(true);
    setRenderError(null);
    
    try {
      const dataUrl = await captureReceipt();
      
      if (!dataUrl || !isMounted.current) {
        throw new Error('Failed to capture receipt for sharing');
      }
      
      const blob = await fetch(dataUrl).then(res => res.blob());
      
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], `recharge-receipt-${transaction?.txnNumber || 'order'}.png`, { 
            type: 'image/png' 
          });
          
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: t('rechargeReceipt.shareTitle') || 'Recharge Receipt',
              text: t('rechargeReceipt.shareText', { 
                amount: parseFloat(transaction?.amount || 0).toFixed(2), 
                receiver: transaction?.receiver 
              }) || `Recharge receipt for ${transaction?.receiver}`,
              files: [file]
            });
          } else {
            await downloadAsImage();
          }
        } catch (shareError) {
          console.error('Share error:', shareError);
          if (shareError.name !== 'AbortError' && isMounted.current) {
            await downloadAsImage();
          }
        }
      } else {
        await downloadAsImage();
      }
    } catch (error) {
      console.error('Error sharing receipt:', error);
      if (isMounted.current) {
        setRenderError(t('rechargeReceipt.shareError') || 'Failed to share. Downloading instead...');
        await downloadAsImage();
      }
    } finally {
      if (isMounted.current) {
        setIsSharing(false);
      }
    }
  }, [transaction, isSharing, captureReceipt, downloadAsImage, cleanupOperations, t]);

  const printReceipt = useCallback(async () => {
    if (!receiptRef.current || isPrinting || !isMounted.current) return;
    
    cleanupOperations();
    
    setIsPrinting(true);
    setRenderError(null);
    
    try {
      const receiptElement = receiptRef.current;
      const clonedContent = receiptElement.cloneNode(true);
      
      const scripts = clonedContent.querySelectorAll('script');
      scripts.forEach(script => script.remove());
      
      const iframes = clonedContent.querySelectorAll('iframe');
      iframes.forEach(iframe => iframe.remove());
      
      const canvases = clonedContent.querySelectorAll('canvas');
      canvases.forEach(canvas => canvas.remove());
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error(t('rechargeReceipt.popupBlocked') || 'Popup blocker prevented printing');
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${t('rechargeReceipt.printTitle') || 'Recharge Receipt'} - ${transaction?.txnNumber || 'Order'}</title>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              /* Reset and base styles */
              * { 
                margin: 0; 
                padding: 0; 
                box-sizing: border-box; 
              }
              
              body { 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
                padding: 20px;
                background: ${isDark ? '#1f2937' : 'white'};
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              
              .receipt-container {
                max-width: 400px;
                width: 100%;
                background: ${isDark ? '#1f2937' : 'white'};
                padding: 24px;
                border-radius: 8px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
              
              /* Utility classes */
              .text-center { text-align: center; }
              .mb-6 { margin-bottom: 24px; }
              .py-2 { padding: 8px 0; }
              .px-4 { padding: 0 16px; }
              .rounded-full { border-radius: 9999px; }
              .bg-green-100 { background-color: #dcfce7; }
              .bg-red-100 { background-color: #fee2e2; }
              .bg-yellow-100 { background-color: #fef9c3; }
              .bg-secondary-100 { background-color: #dbeafe; }
              .bg-gray-100 { background-color: #f3f4f6; }
              .text-green-800 { color: #166534; }
              .text-red-800 { color: #991b1b; }
              .text-yellow-800 { color: #854d0e; }
              .text-secondary-800 { color: #1e40af; }
              .text-gray-800 { color: #1f2937; }
              .text-gray-600 { color: #4b5563; }
              .text-gray-500 { color: #6b7280; }
              .flex { display: flex; }
              .justify-between { justify-content: space-between; }
              .items-center { align-items: center; }
              .space-y-4 > * + * { margin-top: 16px; }
              .border-t { border-top: 1px solid ${isDark ? '#374151' : '#e5e7eb'}; }
              .pt-4 { padding-top: 16px; }
              .mt-4 { margin-top: 16px; }
              .font-mono { font-family: 'Courier New', monospace; }
              .font-bold { font-weight: bold; }
              .font-semibold { font-weight: 600; }
              .font-medium { font-weight: 500; }
              .text-sm { font-size: 14px; }
              .text-lg { font-size: 18px; }
              .text-xl { font-size: 20px; }
              .mr-2 { margin-right: 8px; }
              .capitalize { text-transform: capitalize; }
              .break-words { word-wrap: break-word; }
              .gap-2 { gap: 8px; }
              .text-right { text-align: right; }
              .max-w-\\[200px\\] { max-width: 200px; }
              img { 
                max-width: 100%; 
                height: auto; 
              }
              
              /* Dark mode styles for print */
              @media print {
                body { 
                  padding: 0; 
                  background: white;
                }
                .receipt-container { 
                  box-shadow: none; 
                  padding: 16px;
                  background: white;
                }
                .text-gray-800, .text-gray-600, .text-gray-500 {
                  color: #1f2937 !important;
                }
              }
            </style>
          </head>
          <body>
            <div class="receipt-container">
              ${clonedContent.innerHTML}
            </div>
            <script>
              // Auto-trigger print when ready
              (function() {
                let attempts = 0;
                const maxAttempts = 10;
                
                function tryPrint() {
                  if (document.readyState === 'complete') {
                    window.print();
                    setTimeout(function() { 
                      window.close(); 
                    }, 500);
                  } else if (attempts < maxAttempts) {
                    attempts++;
                    setTimeout(tryPrint, 100);
                  } else {
                    // Force print after timeout
                    window.print();
                    setTimeout(function() { 
                      window.close(); 
                    }, 500);
                  }
                }
                
                tryPrint();
              })();
            <\/script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    } catch (error) {
      console.error('Error printing receipt:', error);
      if (isMounted.current) {
        setRenderError(t('rechargeReceipt.printError') || 'Failed to print. Please try using download instead.');
      }
    } finally {
      if (isMounted.current) {
        setIsPrinting(false);
      }
    }
  }, [transaction?.txnNumber, isPrinting, cleanupOperations, t, isDark]);

  const copyTransactionId = useCallback(async () => {
    if (!transaction?.txnNumber) return;
    
    try {
      await navigator.clipboard.writeText(transaction.txnNumber);
      setCopied(true);
      setTimeout(() => {
        if (isMounted.current) {
          setCopied(false);
        }
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }, [transaction?.txnNumber]);

  if (!isOpen || !transaction) return null;

  const animationData = getAnimation();
  const areButtonsEnabled = isOpen && isReady;

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 ${isDark ? 'dark' : ''}`}>
      <div className={`rounded-lg shadow-xl w-full max-w-[500px] max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
        isDark ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className={`flex justify-between items-center p-4 border-b sticky top-0 z-10 transition-colors duration-300 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-xl font-semibold transition-colors duration-300 ${
            isDark ? 'text-white' : 'text-gray-800'
          }`}>
            {t('rechargeReceipt.title') || 'Recharge Receipt'}
          </h2>
          <button 
            onClick={onClose}
            className={`transition-colors disabled:opacity-50 ${
              isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
            }`}
            disabled={isSharing || isDownloading || isPrinting}
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {renderError && (
          <div className="mx-4 mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <p className="text-sm text-yellow-700 dark:text-yellow-300">{renderError}</p>
          </div>
        )}

        <div ref={receiptRef} className={`p-6 rounded-lg transition-colors duration-300 ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <img
                className="w-auto h-[40px]"
                src="/images/afh-logo.png"
                alt={t('common.logoAlt') || 'Logo'}
                width={140}
                height={40}
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            {animationData && (
              <div className="flex justify-center mb-4">
                <Lottie 
                  animationData={animationData} 
                  loop={false}
                  style={{ width: 200, height: 200 }}
                />
              </div>
            )}
          </div>

          <div className={`text-center py-2 px-4 rounded-full mb-6 font-semibold inline-block w-full mx-auto ${getStatusColor(transaction.status)}`}>
            {t(`recharge2.status.${transaction.status?.toLowerCase() || 'pending'}`)?.toUpperCase() || transaction.status?.toUpperCase()}
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className={`flex items-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <Hash size={15} className="mr-2" />
                <span className="text-[15px]">{t('rechargeReceipt.transactionId') || 'Transaction ID'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`font-mono text-[15px] break-words max-w-[200px] text-right transition-colors duration-300 ${
                  isDark ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  {transaction.txnNumber}
                </span>
                <button
                  onClick={copyTransactionId}
                  className={`p-1 rounded transition-colors ${
                    isDark ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }`}
                  title={t('common.copy') || 'Copy'}
                >
                  {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className={`flex items-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <Calendar size={16} className="mr-2" />
                <span className="text-[15px]">{t('rechargeReceipt.dateTime') || 'Date & Time'}</span>
              </div>
              <div className="text-right">
                <div className={`text-[15px] transition-colors duration-300 ${
                  isDark ? 'text-gray-200' : 'text-gray-800'
                }`}>{date}</div>
                <div className={`text-sm transition-colors duration-300 ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>{time}</div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className={`flex items-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <User size={16} className="mr-2" />
                <span className="text-[15px]">{t('rechargeReceipt.receiver') || 'Receiver'}</span>
              </div>
              <span className={`font-medium text-[15px] break-words max-w-[200px] text-right transition-colors duration-300 ${
                isDark ? 'text-gray-200' : 'text-gray-800'
              }`}>
                {transaction.receiver}
              </span>
            </div>

            {transaction.productDetails?.description && (
              <div className="flex justify-between items-start">
                <span className={`text-[15px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('rechargeReceipt.description') || 'Description'}
                </span>
                <span className={`font-medium text-[15px] text-right max-w-[200px] break-words transition-colors duration-300 ${
                  isDark ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  {transaction.productDetails.description}
                </span>
              </div>
            )}

            {transaction.meta?.error && (
              <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/30 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400 break-words">{transaction.meta.error}</p>
              </div>
            )}

            <div className={`border-t pt-4 mt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center text-lg font-bold">
                <span className={`transition-colors duration-300 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {t('rechargeReceipt.amount') || 'Amount'}
                </span>
                <span className="text-red-600 dark:text-red-400">
                  {parseFloat(transaction.amount || 0).toFixed(2)} {transaction.currency || t('common.currency') || 'AF'}
                </span>
              </div>
            </div>
          </div>

          <div className={`text-center text-sm border-t pt-4 mt-4 ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
            <p>{t('rechargeReceipt.thankYou') || 'Thank you for your recharge!'}</p>
            <p>{t('rechargeReceipt.support') || 'Support'}: support@yes-charge.com</p>
          </div>
        </div>

        <div className={`flex flex-wrap justify-center gap-3 p-4 border-t sticky bottom-0 transition-colors duration-300 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <button
            onClick={downloadAsImage}
            disabled={!areButtonsEnabled || isDownloading || isSharing || isPrinting}
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              !areButtonsEnabled || isDownloading || isSharing || isPrinting
                ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 dark:text-gray-400 cursor-not-allowed'
                : 'bg-gray-600 dark:bg-gray-700 text-white hover:bg-gray-700 dark:hover:bg-gray-600'
            }`}
          >
            <Download size={16} className="mr-2" />
            {isDownloading ? (t('common.downloading') || 'Downloading...') : (t('common.download') || 'Download')}
          </button>
          
          <button
            onClick={printReceipt}
            disabled={!areButtonsEnabled || isPrinting || isDownloading || isSharing}
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              !areButtonsEnabled || isPrinting || isDownloading || isSharing
                ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 dark:text-gray-400 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-[#df2323] dark:bg-primary-600 dark:hover:bg-primary-700'
            }`}
          >
            <Printer size={16} className="mr-2" />
            {isPrinting ? (t('common.printing') || 'Printing...') : (t('common.print') || 'Print')}
          </button>

          <button
            onClick={shareReceipt}
            disabled={!areButtonsEnabled || isSharing || isDownloading || isPrinting}
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              !areButtonsEnabled || isSharing || isDownloading || isPrinting
                ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 dark:text-gray-400 cursor-not-allowed'
                : 'bg-secondary-600 text-white hover:bg-secondary-700 dark:bg-secondary-700 dark:hover:bg-secondary-600'
            }`}
          >
            <Share2 size={16} className="mr-2" />
            {isSharing ? (t('common.sharing') || 'Sharing...') : (t('common.share') || 'Share')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RechargeReceipt;